import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Typography,
  Divider,
  IconButton,
  Box,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import scriptRunnerV2Service from "../../../services/scriptRunnerV2.service";

const DownloadSelectionDialog = ({ open, handleClose, executionId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // stores file key being downloaded

  const fetchFiles = useCallback(async () => {
    if (!executionId) return;
    try {
      setLoading(true);
      const fileList =
        await scriptRunnerV2Service.listExecutionFiles(executionId);
      setFiles(fileList);
    } catch (error) {
      console.error("Failed to fetch execution files", error);
    } finally {
      setLoading(false);
    }
  }, [executionId]);

  useEffect(() => {
    if (open) {
      fetchFiles();
    }
  }, [open, fetchFiles]);

  const handleDownloadSingle = async (file) => {
    try {
      setDownloading(file.key);
      const blob = await scriptRunnerV2Service.downloadSingleFile(
        executionId,
        file.key,
      );
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Single file download failed", error);
      alert("Failed to download file.");
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadAll = async () => {
    try {
      setDownloading("all");
      const blob =
        await scriptRunnerV2Service.downloadExecutionFiles(executionId);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `execution_${executionId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("ZIP download failed", error);
      alert("Failed to download all files.");
    } finally {
      setDownloading(null);
    }
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        component="div"
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Download Execution Results</Typography>
        <IconButton onClick={handleClose} size="small" autoFocus>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : files.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="textSecondary">
              No individual files found for this execution.
            </Typography>
          </Box>
        ) : (
          <List sx={{ pt: 0 }}>
            {files.map((file) => (
              <ListItem
                key={file.key}
                divider
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleDownloadSingle(file)}
                    disabled={!!downloading}
                  >
                    {downloading === file.key ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )}
                  </IconButton>
                }
              >
                <ListItemIcon>
                  <InsertDriveFileIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={formatSize(file.size)}
                  primaryTypographyProps={{ sx: { fontWeight: 500 } }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: "#f9f9f9" }}>
        <Button
          variant="contained"
          startIcon={
            downloading === "all" ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <FolderZipIcon />
            )
          }
          onClick={handleDownloadAll}
          disabled={!!downloading || loading}
          sx={{
            backgroundColor: "#1d9f9f",
            "&:hover": { backgroundColor: "#188182" },
          }}
        >
          Download All (ZIP)
        </Button>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadSelectionDialog;
